<template>
    <div
        v-if="showPagination"
        class="Pagination"
    >
        <div :class="buttonClass">
            <template v-if="!isLoading">
                <nav-arrow
                    direction="left"
                    :disabled="pagination.currentPage === 1"
                    @page-left="navToPage(pagination.currentPage - 1)"
                />

                <template v-if="pagination.totalPages < 8">
                    <NavButton
                        v-for="page in pagination.totalPages"
                        :key="page"
                        :display-number="page"
                        :is-active="pagination.currentPage === page"
                        @page="p => navToPage(p)"
                    />
                </template>
                <template v-else>
                    <NavButton
                        v-for="page in initialButtonSet"
                        :key="page"
                        :display-number="page"
                        :is-active="pagination.currentPage === page"
                        @page="p => navToPage(p)"
                    />

                    <template v-if="middleButtonSet.length > 0">
                        <span
                            v-if="showInitialEllipsis"
                            class="Pagination__ellipsis"
                        >...</span>
                        <NavButton
                            v-for="page in middleButtonSet"
                            :key="page"
                            :display-number="page"
                            :is-active="pagination.currentPage === page"
                            @page="p => navToPage(p)"
                        />
                        <span
                            v-if="showFinalEllipsis"
                            class="Pagination__ellipsis"
                        >...</span>
                    </template>
                    <span
                        v-else
                        class="Pagination__ellipsis"
                    >...</span>

                    <NavButton
                        v-for="page in finalButtonSet"
                        :key="page"
                        :display-number="page"
                        :is-active="pagination.currentPage === page"
                        @page="p => navToPage(p)"
                    />
                </template>


                <nav-arrow
                    direction="right"
                    :disabled="pagination.currentPage === pagination.totalPages"
                    @page-right="navToPage(pagination.currentPage + 1)"
                />
            </template>
        </div>
        <small
            v-if="showTotal"
            :class="totalClass"
        >
            <template v-if="!isLoading">Showing {{ items.count }} of {{ items.total }}</template>
        </small>
    </div>
</template>
<script>
import has from 'lodash/has';
import NavArrow from './components/_navArrows';
import NavButton from './components/_navButton';
import stat from 'status/index';
import statusBuilder from 'status/statusBuilder';

export default {
    name: 'Pagination',
    components: {
        NavArrow,
        NavButton
    },
    props: {
        pagination: {
            type: Object,
            default: () => ({totalPages: -1, currentPage: -1}),
            validator: (obj) => has(obj, 'totalPages') && has(obj, 'currentPage'),
        },
        items: {
            type: Object,
            default: () => ({total: -1, start: -1, end: -1, name: ''}),
            validator: (obj) => has(obj, 'total') && has(obj, 'start') && has(obj, 'end') && has(obj, 'name'),
        },
        showTotal: {
            type: Boolean,
            default: false,
        },
        status: {
            type: String,
            default: stat.STANDBY,
        },
    },
    computed: {
        isLoading(){
            return this.status === stat.LOADING;
        },
        showPagination(){
            return this.pagination.totalPages > 1;
        },
        buttonClass(){
            return statusBuilder('Pagination__button-row', this.status);
        },
        totalClass(){
            return statusBuilder('Pagination__total-row', this.status);
        },
        initialButtonSet(){
            return this.pagination.currentPage > 3 ? [1, 2] : [1, 2, 3];
        },
        finalButtonSet(){
            return this.pagination.currentPage < this.pagination.totalPages -1
                ? [this.pagination.totalPages - 1, this.pagination.totalPages]
                : [this.pagination.totalPages - 2, this.pagination.totalPages - 1, this.pagination.totalPages];
        },
        middleButtonSet(){
            if(this.initialButtonSet.includes(this.pagination.currentPage) || this.finalButtonSet.includes(this.pagination.currentPage)) {
                return [];
            }
            return [this.pagination.currentPage - 1, this.pagination.currentPage, this.pagination.currentPage + 1];
        },
        showInitialEllipsis(){
            return this.middleButtonSet.length > 0 &&
            this.initialButtonSet[this.initialButtonSet.length - 1] !== this.middleButtonSet[0] - 1;
        },
        showFinalEllipsis(){
            return this.middleButtonSet.length > 0 &&
          this.middleButtonSet[this.middleButtonSet.length - 1] !== this.finalButtonSet[0] - 1;
        }
    },
    methods: {
        navToPage(page){
            this.$emit('navToPage', page);
        }
    }
};
</script>
<style lang="scss">
    .Pagination {
        display:flex;
        flex-direction: column;
        align-items: center;
        &__button-row,
        &__total-row {
            display: flex;
            margin-bottom: 1rem;
            align-items: center;
        }
        &__button-row {
            @include loading(2rem, 15rem)
        }
        &__total-row {
            @include loading(11px, 5.5rem)
        }
        &__ellipsis {
            margin: 0 .25rem;
            color: $text-grey;
        }
    }
    .NavArrow {
        outline: none;
        cursor: pointer;
        margin: 0 .25rem;
        height: 1rem;
        width: 1rem;
        border-radius: 4px;
        border-width: 0;
        background: $text-link;
        padding:0;
        display: flex;

        &--disabled {
            cursor: default;
            background: $grey-200
        }
        &__arrow {
            margin: auto;
            &--left { transform: rotate(90deg); }
            &--right { transform: rotate(-90deg); }
        }
    }
    .NavButton {
        cursor: pointer;
        margin: 0 .25rem;
        padding: 0;
        height: 1.5rem;
        width: 1.5rem;
        border: 1px solid $grey-200;
        box-sizing: border-box;
        border-radius: 4px;
        text-align: center;
        color: $text-grey;
        font-size: .80rem;
        &:hover {
            border-color: $text-link;
            background: $text-link;
            color: $white;
        }

        &--active {
            border-width: 0;
            background: $white;
            color: $text-link;
            cursor: default;
            &:hover {
                background: $white;
                color: $text-link;
            }
        }
    }
</style>
